<script setup lang="ts">
  import { computed } from 'vue';

  import BvBox from '@bv/components/box/BvBox.vue';
  import BvErrorMsg from '@bv/components/form/inputs/partials/BvErrorMsg.vue';
  import { ensureArray } from '@bv/utils';

  import type { ErrorObject } from '@vuelidate/core';

  const props = withDefaults(
    defineProps<{
      label?: string;
      size?: 'sm' | 'md';
      error?: boolean;
      errorMessages?: string | string[] | ErrorObject | ErrorObject[];
      disabled?: boolean;
      required?: boolean;
      focused?: boolean;
    }>(),
    {
      label: undefined,
      size: 'md',
      error: false,
      errorMessages: undefined,
      disabled: false,
      required: false,
      focused: false,
    },
  );

  const hasError = computed<boolean>(() => {
    return props.error || ensureArray(props.errorMessages).filter(Boolean).length > 0;
  });
</script>

<template>
  <div class="">
    <bv-box
      border-color="var(--color-gray-400)"
      class="bv-input"
      :class="{
        'bv-input--disabled': props.disabled,
        'bv-input--focused': props.focused,
        'bv-input--error': hasError,
        'bv-input--sm': size === 'sm',
        'bv-input--md': size === 'md',
      }"
      color="var(--color-gray-400)"
      :label="label"
      label-size="0.75rem"
      :required="required"
    >
      <template
        v-if="label"
        #label
      >
        {{ label }}
        <span
          v-if="required"
          class="text-error-500 gap-1"
        >
          <strong>*</strong>
        </span>
      </template>

      <div class="bv-input-label-content">
        <slot name="input" />

        <div
          v-if="$slots['append-icon']"
          class="bv-input-append-icon"
        >
          <slot name="append-icon" />
        </div>
      </div>
    </bv-box>
    <bv-error-msg :errors="errorMessages" />
  </div>
</template>

<style lang="scss" scoped>
  :deep(.bv-box) {
    --bv-box-border-width: var(--bv-input-border-width);
    --bv-box-label-color: var(--bv-input-label-color);
    --bv-box-border-color: var(--bv-input-border-color);
    --bv-box-min-height: var(--bv-input-height);

    --bv-box-padding-top: var(--bv-input-padding-top);
    --bv-box-padding-bottom: var(--bv-input-padding-bottom);
    --bv-box-padding-inline-start: var(--bv-input-padding-inline-start);
    --bv-box-padding-inline-end: var(--bv-input-padding-inline-end);

    &:has(.bv-input-append-icon) {
      z-index: 1;
      --bv-box-padding-inline-end: calc(var(--bv-input-height) + var(--bv-input-gap));
    }

    input::placeholder {
      color: var(--bv-input-placeholder-color);
    }

    --bv-box-bg-color: var(--bv-input-bg-color);
    --bv-box-border-color: var(--bv-input-border-color);
    --bv-box-label-bg-color: var(--bv-input-bg-color);
    --bv-box-label-padding: var(--bv-input-label-padding);
    --bv-box-label-color: var(--bv-input-label-color);

    .bv-input-label-content {
      display: flex;
      gap: var(--bv-input-gap);
      // height: calc(var(--bv-input-height) - var(--bv-box-padding-top) - var(--bv-box-padding-bottom));

      .bv-input-append-icon {
        position: absolute;
        top: 50%;
        inset-inline-end: var(--bv-input-padding-inline-end);
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--bv-input-icon-bg-color);
        width: var(--bv-input-icon-container-size);
        height: var(--bv-input-icon-container-size);
        border-radius: var(--bv-input-icon-radius);
        aspect-ratio: 1/1;
        & > * {
          color: var(--bv-input-icon-color);
          font-size: var(--bv-input-icon-size);
          height: var(--bv-input-icon-size);
          width: var(--bv-input-icon-size);
        }
      }
    }
  }
</style>
